import type { Trade } from '@/models/registry';
import type { ResellType } from '@/models/types';
import { useEffect, useState } from 'react';
import { TitlePresentSelector } from '../title/title-present-selector';
import { ResellTypeSelectorCombobox } from './resell-type-selector-combobox';
import TradeVehicleAppraisedValueEditor from './trade-vehicle-appraised-value-editor copy';
import TradeVehicleConditionEditor from './trade-vehicle-condition-editor';
import TradeVehicleExteriorColorEditor from './trade-vehicle-exterior-color-editor';
import TradeVehicleInteriorColorEditor from './trade-vehicle-interior-color-editor';
import TradeVehicleLienholderEditor from './trade-vehicle-lienholder-editor';
import TradeVehicleMakeEditor from './trade-vehicle-make-editor';
import TradeVehicleMileageEditor from './trade-vehicle-mileage-editor';
import TradeVehicleModelEditor from './trade-vehicle-model-editor';
import TradeVehicleNumberOfKeysEditor from './trade-vehicle-number-of-keys';
import TradeVehiclePayoffAmountEditor from './trade-vehicle-payoff-amount-editor';
import TradeVehicleTrimEditor from './trade-vehicle-trim-editor';
import TradeVehicleVinEditor from './trade-vehicle-vin-editor';
import TradeVehicleYearEditor from './trade-vehicle-year-editor';

// "vin": "1234567890",
// "make": "Toyota",
// "model": "Camry",
// "trim": "XLE",
// "year": 2024,
// "mileage": 10000,
// "condition": "excellent",
// "number_of_keys": 2,
// "exterior_color": "red",
// "interior_color": "black",
// "title_present": true,
// "lienholder": "John Doe",
// "payoff_amount": 10000,
//  "appraised_value": 10000

const Row = ({ children }: { children: React.ReactNode }) => {
  return (
    <div className="flex flex-row w-full gap-1 justify-between flex-wrap">
      {children}
    </div>
  );
};

const TradeVehicleEditor = ({
  tradeId,
  vin,
  make,
  model,
  trim,
  year,
  mileage,
  condition,
  numberOfKeys,
  exteriorColor,
  interiorColor,
  titlePresent,
  lienholder,
  payoffAmount,
  appraisedValue,
  resellType,
}: {
  tradeId: Trade['id'];
  vin: string;
  make: string;
  model: string;
  trim: string;
  year: number;
  mileage: number;
  condition: string;
  numberOfKeys: number;
  exteriorColor: string;
  interiorColor: string;
  titlePresent: boolean;
  lienholder: string;
  payoffAmount: number;
  appraisedValue: number;
  resellType: ResellType;
}) => {
  const [titlePresentValue, setTitlePresentValue] = useState(titlePresent);
  const [resellTypeValue, setResellTypeValue] = useState(resellType);

  useEffect(() => {
    setTitlePresentValue(titlePresent);
  }, [titlePresent]);

  useEffect(() => {
    setResellTypeValue(resellType);
  }, [resellType]);

  return (
    <div className="flex flex-col gap-1 dark:bg-dark dark:text-white">
      <div className="flex flex-col w-full gap-1 justify-between flex-wrap">
        <TradeVehicleYearEditor tradeId={tradeId} initialYear={year} />

        <TradeVehicleMakeEditor tradeId={tradeId} initialMake={make} />

        <TradeVehicleModelEditor tradeId={tradeId} initialModel={model} />

        <TradeVehicleTrimEditor tradeId={tradeId} initialTrim={trim} />

        <TradeVehicleVinEditor tradeId={tradeId} initialVin={vin} />
      </div>

      <TradeVehicleMileageEditor tradeId={tradeId} initialMileage={mileage} />

      <TradeVehicleConditionEditor
        tradeId={tradeId}
        initialCondition={condition}
      />

      <TradeVehicleNumberOfKeysEditor
        tradeId={tradeId}
        initialNumberOfKeys={numberOfKeys}
      />

      <TradeVehicleExteriorColorEditor
        tradeId={tradeId}
        initialExteriorColor={exteriorColor}
      />

      <TradeVehicleInteriorColorEditor
        tradeId={tradeId}
        initialInteriorColor={interiorColor}
      />

      <div className="flex flex-col gap-1">
        <div className="flex flex-row gap-1 items-center flex-wrap justify-between">
          <Row>
            <label className="text-sm text-gray-500 dark:text-white">
              Title Present
            </label>
            <TitlePresentSelector
              tradeId={tradeId}
              titlePresent={titlePresentValue}
              setTitlePresent={setTitlePresentValue}
            />
          </Row>
        </div>
      </div>

      <TradeVehicleLienholderEditor
        tradeId={tradeId}
        initialLienholder={lienholder}
      />

      <Row>
        <label className="text-sm text-gray-500 dark:text-white">
          Resell Type
        </label>
        <ResellTypeSelectorCombobox
          tradeId={tradeId}
          value={resellTypeValue}
          setValue={setResellTypeValue}
        />
      </Row>

      <TradeVehiclePayoffAmountEditor
        tradeId={tradeId}
        initialPayoffAmount={payoffAmount}
      />

      <TradeVehicleAppraisedValueEditor
        tradeId={tradeId}
        initialAppraisedValue={appraisedValue}
      />
    </div>
  );
};

export default TradeVehicleEditor;
